import React, { useState, useEffect } from 'react';
import './DownloadsManager.css';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function DownloadsManager() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/dashboard/downloads`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      setFiles(data.files || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching files:', error);
      setFiles([]);
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length === 0) return;

    setUploading(true);

    try {
      const formData = new FormData();
      selectedFiles.forEach(file => formData.append('files', file));

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/dashboard/downloads/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        alert(`Successfully uploaded ${data.files?.length || selectedFiles.length} file(s)`);
        fetchFiles();
      } else {
        alert('Upload failed: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Upload failed: ' + error.message);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`Delete ${filename}?`)) return;

    try {
      const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/api/admin/dashboard/downloads`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ filename })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        alert('File deleted successfully');
        fetchFiles();
      } else {
        alert('Delete failed: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Delete failed: ' + error.message);
    }
  };

  const copyPath = (path) => {
    if (!path) {
      alert('No path to copy');
      return;
    }
    
    navigator.clipboard.writeText(path);
    alert(`Copied: ${path}`);
  };

  // Get file icon based on extension
  const getFileIcon = (filename) => {
    if (!filename) return '📁';
    if (filename.endsWith('.pdf')) return '📄';
    if (filename.endsWith('.zip')) return '📦';
    if (filename.endsWith('.epub')) return '📖';
    if (filename.endsWith('.mp3')) return '🎵';
    if (filename.endsWith('.mp4')) return '🎬';
    return '📁';
  };

  // Format file size safely
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) return <div className="loading">Loading files...</div>;

  return (
    <div className="downloads-manager">
      <div className="downloads-header">
        <h2>Download Files ({files.length})</h2>
        <label className="upload-btn" style={{ cursor: uploading ? 'not-allowed' : 'pointer' }}>
          {uploading ? 'Uploading...' : 'Upload Files'}
          <input
            type="file"
            multiple
            accept=".pdf,.zip,.epub,.mp3,.mp4"
            onChange={handleUpload}
            disabled={uploading}
            style={{ display: 'none' }}
          />
        </label>
      </div>

      <div className="files-list">
        {files.map((file, index) => (
          <div key={file.filename || index} className="file-card">
            <div className="file-info">
              <div className="file-icon">
                {getFileIcon(file.filename)}
              </div>
              <div className="file-details">
                <div className="file-name">{file.filename || 'Unknown file'}</div>
                <div className="file-meta">
                  {formatFileSize(file.size)} • {file.created ? new Date(file.created).toLocaleDateString() : 'Unknown date'}
                </div>
              </div>
            </div>
            <div className="file-actions">
              <button onClick={() => copyPath(file.path)} className="copy-btn">
                Copy Path
              </button>
              <button onClick={() => handleDelete(file.filename)} className="delete-btn">
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {files.length === 0 && (
        <div className="empty-state">
          <p>No downloadable files yet. Upload PDFs, ZIPs, or other files to get started!</p>
        </div>
      )}
    </div>
  );
}